/**
 * Date Localization Utilities
 *
 * Provides consistent date/time formatting based on the current portal language.
 * Automatically formats timestamps in regional formats:
 * - English (en): US format (MM/dd/yyyy, h:mm AM/PM)
 * - German (de): DE format (dd.MM.yyyy, HH:mm)
 * - French (fr): FR format (dd/MM/yyyy, HH:mm)
 * - Spanish (es): ES format (dd/MM/yyyy, HH:mm)
 * - Portuguese (pt): PT format (dd/MM/yyyy, HH:mm)
 */

import { Language } from "../types/i18n";

/**
 * Get the appropriate locale string for JavaScript Intl API
 */
export function getLocaleString(language: Language): string {
  const localeMap: Record<Language, string> = {
    en: "en-US",
    de: "de-DE",
    fr: "fr-FR",
    es: "es-ES",
    pt: "pt-PT",
  };

  return localeMap[language] || "en-US";
}

/**
 * Format timestamp for chart X-axis (short format)
 * - English: "Oct 01"
 * - German: "01. Okt"
 * - French: "01 oct."
 * - Spanish: "01 oct"
 * - Portuguese: "01 out"
 */
export function formatChartDate(timestamp: string, language: Language): string {
  if (!timestamp) return "Invalid Date";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "Invalid Date";
  const locale = getLocaleString(language);

  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
  });
}

/**
 * Format timestamp for tooltips and detailed displays
 * - English: "Oct 01, 3:44 PM"
 * - German: "01. Okt, 15:44"
 * - French: "01 oct., 15:44"
 * - Spanish: "01 oct, 15:44"
 * - Portuguese: "01 out, 15:44"
 */
export function formatTooltipTimestamp(
  timestamp: string,
  language: Language,
): string {
  if (!timestamp) return "Invalid Date";
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "Invalid Date";
  const locale = getLocaleString(language);

  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: language === "en",
  });
}

/**
 * Format timestamp for full date displays
 * - English: "10/01/2025, 3:44 PM"
 * - German: "01.10.2025, 15:44"
 * - French: "01/10/2025, 15:44"
 * - Spanish: "01/10/2025, 15:44"
 * - Portuguese: "01/10/2025, 15:44"
 */
export function formatFullTimestamp(
  timestamp: string,
  language: Language,
): string {
  const date = parseDateValue(timestamp) ?? new Date(NaN);
  const locale = getLocaleString(language);

  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: language === "en", // Only use 12-hour format for English
  });
}

/**
 * Format date only (no time)
 * - English: "10/01/2025"
 * - German: "01.10.2025"
 * - French: "01/10/2025"
 * - Spanish: "01/10/2025"
 * - Portuguese: "01/10/2025"
 */
export function formatDateOnly(timestamp: string, language: Language): string {
  const date = parseDateValue(timestamp) ?? new Date(NaN);
  const locale = getLocaleString(language);

  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Format time only (no date)
 * - English: "3:44 PM"
 * - German: "15:44"
 * - French: "15:44"
 * - Spanish: "15:44"
 * - Portuguese: "15:44"
 */
export function formatTimeOnly(timestamp: string, language: Language): string {
  const date = new Date(timestamp);
  const locale = getLocaleString(language);

  return date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: language === "en", // Only use 12-hour format for English
  });
}

/**
 * Format "Last updated" text
 * - English: "Last updated: 10/01/2025"
 * - German: "Letzte Aktualisierung: 01.10.2025"
 * - French: "Dernière mise à jour: 01/10/2025"
 * - Spanish: "Última actualización: 01/10/2025"
 * - Portuguese: "Última atualização: 01/10/2025"
 */
export function formatLastUpdated(
  timestamp: string,
  language: Language,
): string {
  return formatDateOnly(timestamp, language);
}

// ---------------------------------------------------------------------------
// General-purpose formatters. Every date the app shows goes through these, so
// English renders US conventions (MM/DD/YYYY, 12-hour clock) and the other
// languages their own. Storage stays ISO 8601; these only touch display.
// ---------------------------------------------------------------------------

export type DateValue = string | number | Date | null | undefined;

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** FHIR partial dates: a year, or a year and month (birthDate "1990"). */
const YEAR_ONLY = /^(\d{4})$/;
const YEAR_MONTH = /^(\d{4})-(\d{2})$/;

/**
 * Parse a stored value into a Date. A bare ISO date ("2024-10-01", what FHIR
 * and <input type="date"> carry) is read as a calendar day in local time:
 * `new Date("2024-10-01")` is UTC midnight, which every timezone west of UTC
 * would show as September 30.
 */
export function parseDateValue(value: DateValue): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    const text = value.trim();
    const m = DATE_ONLY.exec(text);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    // Read as local too: new Date("1990") is UTC, i.e. 31 December 1989 in
    // every timezone west of UTC.
    const ym = YEAR_MONTH.exec(text);
    if (ym) return new Date(Number(ym[1]), Number(ym[2]) - 1, 1);
    const y = YEAR_ONLY.exec(text);
    if (y) return new Date(Number(y[1]), 0, 1);
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

export type DateStyle = "numeric" | "medium" | "long" | "monthYear";

const DATE_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  // 10/01/2024 · 01.10.2024
  numeric: { day: "2-digit", month: "2-digit", year: "numeric" },
  // Oct 1, 2024 · 1. Okt. 2024
  medium: { day: "numeric", month: "short", year: "numeric" },
  // October 1, 2024 · 1. Oktober 2024
  long: { day: "numeric", month: "long", year: "numeric" },
  // October 2024 · Oktober 2024
  monthYear: { month: "long", year: "numeric" },
};

/** A calendar date in the language's convention; "" when there is none. */
export function formatDate(
  value: DateValue,
  language: Language,
  style: DateStyle = "numeric",
): string {
  const date = parseDateValue(value);
  if (!date) return typeof value === "string" ? value : "";
  // A partial date is shown only as far as it is known: "1990" stays 1990,
  // not 01/01/1990, a day nobody recorded.
  if (typeof value === "string") {
    const text = value.trim();
    if (YEAR_ONLY.test(text)) return text;
    if (YEAR_MONTH.test(text)) {
      return date.toLocaleDateString(
        getLocaleString(language),
        DATE_STYLES.monthYear,
      );
    }
  }
  return date.toLocaleDateString(getLocaleString(language), DATE_STYLES[style]);
}

/** A clock time: 3:44 PM in English, 15:44 elsewhere. */
export function formatTime(value: DateValue, language: Language): string {
  const date = parseDateValue(value);
  if (!date) return typeof value === "string" ? value : "";
  return date.toLocaleTimeString(getLocaleString(language), {
    hour: "numeric",
    minute: "2-digit",
    hour12: language === "en",
  });
}

/** Date and time: 10/01/2024, 3:44 PM in English, 01.10.2024, 15:44 in German. */
export function formatDateTime(
  value: DateValue,
  language: Language,
  style: DateStyle = "numeric",
): string {
  const date = parseDateValue(value);
  if (!date) return typeof value === "string" ? value : "";
  return date.toLocaleString(getLocaleString(language), {
    ...DATE_STYLES[style],
    hour: "numeric",
    minute: "2-digit",
    hour12: language === "en",
  });
}

/** A plain number with the language's separators: 1,234.5 · 1.234,5. */
export function formatNumber(
  value: number,
  language: Language,
  maximumFractionDigits = 1,
): string {
  return new Intl.NumberFormat(getLocaleString(language), {
    maximumFractionDigits,
  }).format(value);
}
